import React, { useCallback, useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';
import { toast } from 'react-hot-toast';
import { Crop, ExternalLink, GripVertical, Loader2, Pencil, Plus, RotateCw, Trash2, Upload, Users, X, ZoomIn } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  createTeamMember,
  deleteTeamMember,
  getTeamMembers,
  reorderTeamMembers,
  TeamMember,
  updateTeamMember,
} from '../../services/teamApi';

const TEAM_PHOTO_SIZE = 400;
const DESCRIPTION_MAX = 280;
const NAME_MAX = 80;
const DESIGNATION_MAX = 80;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

interface FormState {
  name: string;
  designation: string;
  linkedinUrl: string;
  description: string;
}

const emptyForm: FormState = {
  name: '',
  designation: '',
  linkedinUrl: '',
  description: '',
};

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    if (url.startsWith('http')) {
      image.crossOrigin = 'anonymous';
    }
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', () => reject(new Error('Could not load the selected photo')));
    image.src = url;
  });

const getCroppedBlob = async (imageSrc: string, pixelCrop: Area, rotation: number): Promise<Blob> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not crop the photo');
  }

  const rotRad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rotRad));
  const cos = Math.abs(Math.cos(rotRad));
  const bBoxWidth = image.width * cos + image.height * sin;
  const bBoxHeight = image.width * sin + image.height * cos;

  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  const output = document.createElement('canvas');
  output.width = TEAM_PHOTO_SIZE;
  output.height = TEAM_PHOTO_SIZE;
  const outCtx = output.getContext('2d');
  if (!outCtx) {
    throw new Error('Could not crop the photo');
  }
  outCtx.imageSmoothingEnabled = true;
  outCtx.imageSmoothingQuality = 'high';
  outCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    TEAM_PHOTO_SIZE,
    TEAM_PHOTO_SIZE
  );

  return new Promise((resolve, reject) => {
    output.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Could not crop the photo'));
        return;
      }
      resolve(blob);
    }, 'image/jpeg', 0.92);
  });
};

const isHttpUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('Could not read the cropped photo'));
    };
    reader.onerror = () => reject(new Error('Could not read the cropped photo'));
    reader.readAsDataURL(blob);
  });

export default function AdminTeams() {
  const formRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);
  const [savedPhotoUrl, setSavedPhotoUrl] = useState<string | null>(null);
  const [memberToDelete, setMemberToDelete] = useState<TeamMember | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  const membersRef = useRef<TeamMember[]>([]);
  const orderBeforeDrag = useRef<string[] | null>(null);

  membersRef.current = members;

  const [cropOpen, setCropOpen] = useState(false);
  const [cropSource, setCropSource] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [applyingCrop, setApplyingCrop] = useState(false);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const nextMembers = await getTeamMembers();
      setMembers(nextMembers);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    return () => {
      if (photoPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [photoPreview]);

  const resetForm = () => {
    setForm(emptyForm);
    setErrors({});
    setEditingId(null);
    setCroppedBlob(null);
    setSavedPhotoUrl(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const closeCrop = () => {
    setCropOpen(false);
    if (cropSource?.startsWith('blob:')) {
      URL.revokeObjectURL(cropSource);
    }
    setCropSource(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePhotoPick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error('Use a JPEG, PNG, WebP, or GIF photo.');
      event.target.value = '';
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error('Photo must be 8 MB or smaller.');
      event.target.value = '';
      return;
    }

    if (cropSource?.startsWith('blob:')) {
      URL.revokeObjectURL(cropSource);
    }
    const sourceUrl = URL.createObjectURL(file);
    setCropSource(sourceUrl);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    setCropOpen(true);
  };

  const applyCrop = async () => {
    if (!cropSource || !croppedAreaPixels) return;
    setApplyingCrop(true);
    try {
      const blob = await getCroppedBlob(cropSource, croppedAreaPixels, rotation);
      const previewUrl = URL.createObjectURL(blob);
      setCroppedBlob(blob);
      setSavedPhotoUrl(null);
      setPhotoPreview(previewUrl);
      setErrors((current) => {
        const next = { ...current };
        delete next.photo;
        return next;
      });
      setCropOpen(false);
      if (cropSource.startsWith('blob:')) {
        URL.revokeObjectURL(cropSource);
      }
      setCropSource(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to crop the photo');
    } finally {
      setApplyingCrop(false);
    }
  };

  const clearPhoto = () => {
    setCroppedBlob(null);
    setSavedPhotoUrl(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validate = () => {
    const next: Record<string, string> = {};
    const name = form.name.trim();
    const designation = form.designation.trim();
    const linkedinUrl = form.linkedinUrl.trim();
    const description = form.description.trim();

    if (!name) next.name = 'Name is required.';
    else if (name.length > NAME_MAX) next.name = `Name must be ${NAME_MAX} characters or fewer.`;

    if (!designation) next.designation = 'Designation is required.';
    else if (designation.length > DESIGNATION_MAX) {
      next.designation = `Designation must be ${DESIGNATION_MAX} characters or fewer.`;
    }

    if (!linkedinUrl || !isHttpUrl(linkedinUrl)) {
      next.linkedinUrl = 'Enter a full LinkedIn URL starting with https://.';
    }

    if (!description) next.description = 'A short description is required.';
    else if (description.length > DESCRIPTION_MAX) {
      next.description = `Description must be ${DESCRIPTION_MAX} characters or fewer.`;
    }

    if (!croppedBlob && !savedPhotoUrl) {
      next.photo = `Add a photo and crop it to ${TEAM_PHOTO_SIZE} × ${TEAM_PHOTO_SIZE}.`;
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      let photoUrl = savedPhotoUrl || '';
      if (croppedBlob) {
        photoUrl = await blobToDataUrl(croppedBlob);
      }

      const payload = {
        name: form.name.trim(),
        designation: form.designation.trim(),
        linkedinUrl: form.linkedinUrl.trim(),
        description: form.description.trim(),
        photoUrl,
      };

      if (editingId) {
        await updateTeamMember(editingId, payload);
        toast.success('Team member updated.');
      } else {
        await createTeamMember(payload);
        toast.success('Team member added.');
      }

      resetForm();
      await loadMembers();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save team member');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (member: TeamMember) => {
    setEditingId(member._id);
    setForm({
      name: member.name,
      designation: member.designation,
      linkedinUrl: member.linkedinUrl,
      description: member.description,
    });
    setErrors({});
    setCroppedBlob(null);
    setSavedPhotoUrl(member.photoUrl);
    setPhotoPreview(member.photoUrl);
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const moveMember = (sourceId: string, targetId: string) => {
    const current = membersRef.current;
    const from = current.findIndex((member) => member._id === sourceId);
    const to = current.findIndex((member) => member._id === targetId);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...current];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    membersRef.current = next;
    setMembers(next);
  };

  const persistOrder = async () => {
    const before = orderBeforeDrag.current;
    const nextMembers = membersRef.current;
    const after = nextMembers.map((member) => member._id);
    orderBeforeDrag.current = null;
    if (!before || before.join('|') === after.join('|')) return;

    setReordering(true);
    try {
      const saved = await reorderTeamMembers(after);
      setMembers(saved);
    } catch (error) {
      const previous = before
        .map((id) => nextMembers.find((member) => member._id === id))
        .filter((member): member is TeamMember => Boolean(member));
      const missing = nextMembers.filter((member) => !before.includes(member._id));
      setMembers([...previous, ...missing]);
      toast.error(error instanceof Error ? error.message : 'Failed to update team order');
    } finally {
      setReordering(false);
    }
  };

  const handleDragStart = (memberId: string, event: React.DragEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('button, a')) {
      event.preventDefault();
      return;
    }
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', memberId);
    orderBeforeDrag.current = membersRef.current.map((member) => member._id);
    setDraggingId(memberId);
  };

  const handleDragOver = (memberId: string, event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (!draggingId || draggingId === memberId || reordering) return;
    moveMember(draggingId, memberId);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    void persistOrder();
  };

  const confirmDelete = async () => {
    if (!memberToDelete) return;
    setDeleting(true);
    try {
      await deleteTeamMember(memberToDelete._id);
      if (editingId === memberToDelete._id) {
        resetForm();
      }
      toast.success('Team member deleted.');
      setMemberToDelete(null);
      await loadMembers();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete team member');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Teams</h1>
        <p className="mt-1 text-sm text-gray-500">
          Add a team member with a photo cropped to a fixed {TEAM_PHOTO_SIZE} × {TEAM_PHOTO_SIZE} square.
        </p>
      </div>

      <div className="grid gap-6 xl:grid-cols-[420px_minmax(0,1fr)]">
        <Card ref={formRef} id="team-form">
          <CardHeader>
            <CardTitle className="text-lg">{editingId ? 'Edit team member' : 'Add team member'}</CardTitle>
            <CardDescription>
              Name, designation, LinkedIn URL, a short description, and a cropped photo are saved to the database.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <Label htmlFor="team-name">Name</Label>
                <Input
                  id="team-name"
                  value={form.name}
                  maxLength={NAME_MAX}
                  placeholder="Full name"
                  onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                />
                {errors.name && <p className="text-sm text-red-600">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="team-designation">Designation</Label>
                <Input
                  id="team-designation"
                  value={form.designation}
                  maxLength={DESIGNATION_MAX}
                  placeholder="Role or title"
                  onChange={(event) => setForm((current) => ({ ...current, designation: event.target.value }))}
                />
                {errors.designation && <p className="text-sm text-red-600">{errors.designation}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="team-linkedin">LinkedIn URL</Label>
                <Input
                  id="team-linkedin"
                  type="url"
                  value={form.linkedinUrl}
                  placeholder="https://www.linkedin.com/in/username"
                  onChange={(event) => setForm((current) => ({ ...current, linkedinUrl: event.target.value }))}
                />
                {errors.linkedinUrl && <p className="text-sm text-red-600">{errors.linkedinUrl}</p>}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="team-description">Short description</Label>
                  <span className="text-xs text-gray-500">
                    {form.description.trim().length}/{DESCRIPTION_MAX}
                  </span>
                </div>
                <Textarea
                  id="team-description"
                  value={form.description}
                  maxLength={DESCRIPTION_MAX}
                  rows={4}
                  placeholder="A short line about this person"
                  onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                />
                {errors.description && <p className="text-sm text-red-600">{errors.description}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="team-photo">Photo</Label>
                <div className="flex items-start gap-4">
                  <div className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-md border border-dashed border-gray-300 bg-gray-50">
                    {photoPreview ? (
                      <img src={photoPreview} alt="Cropped team photo preview" className="h-full w-full object-cover" />
                    ) : (
                      <Upload className="h-6 w-6 text-gray-400" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1 space-y-2">
                    <Input
                      id="team-photo"
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handlePhotoPick}
                    />
                    <p className="text-xs text-gray-500">
                      After you choose a photo, crop it manually. It is saved as {TEAM_PHOTO_SIZE} × {TEAM_PHOTO_SIZE} px.
                    </p>
                    {photoPreview && (
                      <Button type="button" variant="outline" size="sm" onClick={clearPhoto}>
                        <X className="mr-1 h-4 w-4" />
                        Remove photo
                      </Button>
                    )}
                  </div>
                </div>
                {errors.photo && <p className="text-sm text-red-600">{errors.photo}</p>}
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                {editingId && (
                  <Button type="button" variant="outline" onClick={resetForm} disabled={saving}>
                    Cancel
                  </Button>
                )}
                <Button type="submit" className="bg-orange-600 text-white hover:bg-orange-700" disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : editingId ? (
                    'Update member'
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Add member
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Saved members</CardTitle>
            <CardDescription>
              {loading
                ? 'Loading...'
                : `${members.length} ${members.length === 1 ? 'member' : 'members'}. Drag a card to set its position.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-16 text-gray-500">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Loading team members
              </div>
            ) : members.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center text-gray-500">
                <Users className="mb-3 h-10 w-10 text-gray-300" />
                <p>No team members yet. Add the first one with the form.</p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {members.map((member, index) => (
                  <div
                    key={member._id}
                    draggable={!reordering}
                    onDragStart={(event) => handleDragStart(member._id, event)}
                    onDragOver={(event) => handleDragOver(member._id, event)}
                    onDrop={(event) => event.preventDefault()}
                    onDragEnd={handleDragEnd}
                    className={`flex gap-3 rounded-lg border p-3 ${
                      draggingId === member._id ? 'cursor-grabbing opacity-60' : 'cursor-grab'
                    } ${reordering ? 'pointer-events-none opacity-70' : ''}`}
                  >
                    <div className="flex shrink-0 flex-col items-center gap-2 text-gray-400">
                      <GripVertical className="h-4 w-4" />
                      <span className="text-xs font-medium text-gray-500">{index + 1}</span>
                    </div>
                    <img
                      src={member.photoUrl}
                      alt={member.name}
                      draggable={false}
                      className="h-20 w-20 shrink-0 rounded-md object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate font-medium text-gray-900">{member.name}</p>
                          <p className="truncate text-sm text-orange-700">{member.designation}</p>
                        </div>
                        <div className="flex shrink-0 gap-1">
                          <Button type="button" variant="ghost" size="icon" onClick={() => startEdit(member)} aria-label={`Edit ${member.name}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => setMemberToDelete(member)}
                            aria-label={`Delete ${member.name}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                      <p className="mt-1 line-clamp-2 text-sm text-gray-600">{member.description}</p>
                      <a
                        href={member.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-sm text-orange-600 hover:underline"
                      >
                        LinkedIn
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={cropOpen} onOpenChange={(open) => !open && !applyingCrop && closeCrop()}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crop className="h-5 w-5 text-orange-600" />
              Crop photo to {TEAM_PHOTO_SIZE} × {TEAM_PHOTO_SIZE}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative h-[360px] overflow-hidden rounded-lg border bg-gray-100">
              {cropSource && (
                <Cropper
                  image={cropSource}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={1}
                  cropShape="rect"
                  showGrid
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onRotationChange={setRotation}
                  onCropComplete={onCropComplete}
                />
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-sm">
                  <ZoomIn className="h-4 w-4" />
                  Zoom
                </Label>
                <span className="text-sm text-gray-500">{Math.round(zoom * 100)}%</span>
              </div>
              <input
                type="range"
                min="1"
                max="3"
                step="0.05"
                value={zoom}
                onChange={(event) => setZoom(Number(event.target.value))}
                className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-orange-600"
              />
            </div>
            <p className="text-sm text-gray-500">
              Drag the photo inside the square. The saved image is always {TEAM_PHOTO_SIZE} × {TEAM_PHOTO_SIZE} pixels.
            </p>
            <div className="flex items-center justify-between gap-2">
              <Button type="button" variant="outline" onClick={() => setRotation((current) => (current + 90) % 360)}>
                <RotateCw className="mr-2 h-4 w-4" />
                Rotate
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={closeCrop} disabled={applyingCrop}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="bg-orange-600 text-white hover:bg-orange-700"
                  onClick={applyCrop}
                  disabled={!croppedAreaPixels || applyingCrop}
                >
                  {applyingCrop ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Cropping...
                    </>
                  ) : (
                    'Apply crop'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(memberToDelete)} onOpenChange={(open) => !open && !deleting && setMemberToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this team member?</AlertDialogTitle>
            <AlertDialogDescription>
              {memberToDelete ? `${memberToDelete.name} will be removed from the team list.` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
